import { note } from "@clack/prompts";
import type { WakeLoopProfile } from "../config/wakeloop-profiles-api";
import { buildProfileWorkspacePresentation } from "../profile-workspace/presentation";
import {
  type ProfileWorkspaceRuntimeResolution,
  resolveProfileWorkspaceRuntime,
} from "../profile-workspace/runtime-resolution";
import { ensureWakeLoopManagedProfileWorkspace } from "../profile-workspace/service";
import { formatWakeLoopCliCommand } from "../runtime/cli-entry-command";
import type { createPresenter } from "../runtime/presenter/index";
import type { RuntimeContext } from "../runtime/types";

type StartPresenter = ReturnType<typeof createPresenter>;

export async function resolveStartSelectedProfileWorkspace(input: {
  profile: WakeLoopProfile;
}): Promise<ProfileWorkspaceRuntimeResolution | null> {
  if (input.profile.profileKind !== "agent") {
    return null;
  }

  let resolution = await resolveProfileWorkspaceRuntime({
    wakeLoopProfileId: input.profile.wakeLoopProfileId,
    mode: "inspect",
  });
  if (resolution.status === "missing") {
    await ensureWakeLoopManagedProfileWorkspace({
      wakeLoopProfileId: input.profile.wakeLoopProfileId,
    });
    resolution = await resolveProfileWorkspaceRuntime({
      wakeLoopProfileId: input.profile.wakeLoopProfileId,
      mode: "inspect",
    });
  }
  return resolution;
}

export function emitStartSelectedProfileWorkspace(input: {
  presenter: StartPresenter;
  profile: WakeLoopProfile;
  resolvedView: RuntimeContext["resolvedView"];
  resolution: ProfileWorkspaceRuntimeResolution | null;
}): void {
  if (!input.resolution) {
    return;
  }

  const workspace = buildProfileWorkspacePresentation(input.resolution);
  if (!workspace) {
    return;
  }

  const changeCommand = formatWakeLoopCliCommand(
    `wakeloop profiles update --profile ${input.profile.wakeLoopProfileId} --workspace-mode custom --workspace-path /absolute/path`
  );
  const repairCommand = formatWakeLoopCliCommand("wakeloop doctor --repair");
  const summary = resolveStartWorkspaceSummary(workspace.status);

  if (input.resolvedView === "agent") {
    input.presenter.data({
      code: "start.profile.workspace",
      payload: {
        changeCommand,
        repairCommand,
        summary,
        workspace,
      },
    });
    return;
  }

  note(
    buildStartWorkspaceLines({
      changeCommand,
      repairCommand,
      summary,
      workspace,
    }).join("\n"),
    "Profile Workspace",
    {
      format: (value) => value,
    }
  );
}

function buildStartWorkspaceLines(input: {
  changeCommand: string;
  repairCommand: string;
  summary: string;
  workspace: NonNullable<ReturnType<typeof buildProfileWorkspacePresentation>>;
}): string[] {
  if (input.workspace.reasonCode === "workspace.invalid") {
    return [
      input.summary,
      `Workspace Folder: ${input.workspace.configuredWorkspaceDisplay}`,
      `Next: Run ${input.repairCommand}, then re-check with ${formatWakeLoopCliCommand("wakeloop service status --profile <profile-id>")}.`,
    ];
  }

  if (input.workspace.reasonCode === "workspace.missing") {
    return [
      input.summary,
      `Next: Run ${input.repairCommand}, then re-check with ${formatWakeLoopCliCommand("wakeloop service status --profile <profile-id>")}.`,
    ];
  }

  return [
    input.summary,
    `Workspace Folder: ${input.workspace.configuredWorkspaceDisplay}`,
    `You can switch this profile to another folder later with ${input.changeCommand}.`,
  ];
}

function resolveStartWorkspaceSummary(
  status: NonNullable<
    ReturnType<typeof buildProfileWorkspacePresentation>
  >["status"]
): string {
  switch (status) {
    case "wakeloop_managed":
      return "This profile uses a WakeLoop workspace.";
    case "custom":
      return "This profile uses a custom workspace.";
    case "missing":
      return "This profile is missing its local workspace on this computer.";
    case "invalid":
      return "This profile's local workspace is broken on this computer.";
    default:
      return "This profile's workspace needs attention on this computer.";
  }
}
